import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Google Analytics 4 (GA4) integration — env-driven, never hardcodes a fake ID.
 *
 * Set REACT_APP_GA_MEASUREMENT_ID in /app/frontend/.env when ready.
 * Examples of valid IDs: "G-XXXXXXXXXX"
 *
 * - If the env var is missing/blank, this component is a no-op.
 * - When set, it loads gtag.js once and tracks SPA route changes as page_view.
 * - Idempotent: safe under React StrictMode.
 */
export const GoogleAnalytics = () => {
  const location = useLocation();
  const measurementId = (process.env.REACT_APP_GA_MEASUREMENT_ID || '').trim();

  // One-time gtag bootstrap
  useEffect(() => {
    if (!measurementId) return;

    // Avoid duplicate loads (StrictMode, hot reload)
    if (document.getElementById('ga4-script')) return;

    const s = document.createElement('script');
    s.id = 'ga4-script';
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
      measurementId
    )}`;
    document.head.appendChild(s);

    window.dataLayer = window.dataLayer || [];
    function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;
    gtag('js', new Date());
    // send_page_view: false because we send manually on route change
    gtag('config', measurementId, { send_page_view: false });
  }, [measurementId]);

  // Track SPA navigation as page_view
  useEffect(() => {
    if (!measurementId || typeof window.gtag !== 'function') return;
    window.gtag('event', 'page_view', {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search, measurementId]);

  return null;
};

export default GoogleAnalytics;
